import { FC, useState, useMemo, ChangeEvent } from 'react'
import { AttorneyData } from 'src/dtos/applicationData'
import { AboutAttorneyFormContainer, OptionButtonsContainer } from 'src/components/application/defaultComponents'
import { LeftBar } from 'src/components/basic/LeftBar'
import { FormInputErrorMessage, FlexDiv } from 'src/components/application/formComponents/styles'
import StandardInput from 'src/components/basic/StandardInput'
import StandardFormatInput from 'src/components/basic/StandardFormatInput'
import OptionButton from 'src/components/basic/OptionButton'
import StepBullet from 'src/components/basic/StepBullet'
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md'

import { LicensingNewItemFormSchema } from 'src/yup/NewAppSchema'
import { validateDateField, validateOptionalDate } from 'src/utils/formDataManipulation'
import StandardSelect from 'src/components/basic/StandardSelect'
import { states } from 'src/constants/application'
import { ValidationErrorMessage } from 'src/components/basic/ValidationErrorMessage/ValidationErrorMessage'
import { OptionButtonContainer } from 'src/components/basic/OptionButton/styles'
import BarCardNumberInput from 'src/components/basic/BarCardNumberInput'

export type LicensingSectionData = AttorneyData['license']

interface Props {
  attorney: AttorneyData;
  setAttorneyInfo: (changes: Partial<AttorneyData>) => void;
  handleSaveDetails: (section: string) => Promise<void>;
  status: {
    isAccordion: boolean;
    isOpen: boolean;
  }
  isIncorrect?: boolean;
  toggleOpenAccordion?: () => void;
  errorCodes?: string[];
}

const Licensing: FC<Props> = ({
  attorney,
  setAttorneyInfo,
  isIncorrect,
  handleSaveDetails,
  status,
  toggleOpenAccordion,
  errorCodes = [],
}) => {
  const [disabledSaveDetailsButton, setDisabledSaveDetailsButton] = useState(false)

  const [isLicensedInTexasState, setIsLicensedInTexasState] = useState(false);
  const [isLicenseDateRequired, setIsLicenseDateRequired] = useState(false);
  const [licenseDateHasBlurred, setLicenseDateHasBlurred] = useState(false);

  const [isLicensedInOtherState, setIsLicensedInOtherState] = useState(false);
  const [OtherLicenseDateHasBlurred, setOtherLicenseDateHasBlurred] = useState(false);


  const [otherStateHasBlurred, setOtherStateHasBlurred] = useState(false);
  const [percentageOfWorkSpentHasBlurred, setPercentageOfWorkSpentHasBlurred] = useState(false);

  const handleBarCardNumberChange = (event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setAttorneyInfo({ [name]: value })
  }

  const handleInputLicensedInTexasChange = (changes: Partial<AttorneyData['license']>) => {
    if (changes?.isLicensedInTexas === true) {
      setIsLicensedInTexasState(true);
    } else {
      setIsLicensedInTexasState(false);
    }

    setAttorneyInfo({ license: { ...attorney?.license, ...changes } });
  }

  const handleInputLicensedInOtherStateChange = (changes: Partial<AttorneyData['license']>) => {
    if (changes?.isLicensedInOtherState === true) {
      setIsLicenseDateRequired(false);
      setIsLicensedInOtherState(true);
    } else {
      setIsLicenseDateRequired(true);
      setIsLicensedInOtherState(false);
    }

    setAttorneyInfo({ license: { ...attorney?.license, ...changes } });
  }

  const handleInputChange = (changes: Partial<AttorneyData['license']>) => {
    setAttorneyInfo({ license: { ...attorney?.license, ...changes } })
  }

  // const formContainerClasses = `form-container ${
  //   status.isAccordion ?
  //   (status.isOpen ? 'accordion-open' : 'accordion-closed') :
  //   ''
  // }`
  const formContainerClasses = `form-container `

  const onSaveDetailsClick = async () => {
    setDisabledSaveDetailsButton(true)
    await handleSaveDetails('license')
    setDisabledSaveDetailsButton(false)
  }


  const isFormValid = useMemo(() => {
    try {
      LicensingNewItemFormSchema.validateSync(attorney?.license)
      return true
    } catch (e) {
      //const err = e as ValidationError
      return false
    }
  }, [attorney?.license])

  const isInputIncorrect = () => {

    return isIncorrect && !isFormValid;
  }

  function isLicenseDateIncorrect() {
    // Postpone judgement that the input is incorrect until after either:
    // 1) a blur occurs
    //   or:
    // 2) when the whole form is validated (ex: Save & continue)
    // Until either of those, return false (meaning the input is correct)
    if (!licenseDateHasBlurred) {
      return false
    }

    //return false
    if (isLicenseDateRequired && attorney?.license?.licenseDate === '') {
      return true
    }

    //1) blur test
    if (licenseDateHasBlurred
      && validateDateField(attorney?.license?.licenseDate, false, 10, isLicenseDateRequired).length > 0 && isLicenseDateRequired == false) {
      return true;
    }
    //2) the standard whole form check
    return isInputIncorrect();
  }

  function handleOtherStateBlur() {
    setOtherStateHasBlurred(true);
  }
  function handlePercentageOfWorkSpentBlur() {
    setPercentageOfWorkSpentHasBlurred(true);
  }

  function handleLicenseDateBlur() {
    setLicenseDateHasBlurred(true);
  }

  function handleOtherLicenseDateBlur() {
    setOtherLicenseDateHasBlurred(true);
  }

  function decideLicenseDateErrorMessage() {
    if (isLicenseDateIncorrect()) {
      return validateDateField(attorney?.license?.licenseDate, false, 10, isLicenseDateRequired)
    }
    return '';
  }

  let licenseDateErrMsg = licenseDateHasBlurred ? validateDateField(attorney?.license?.licenseDate, false, 10, isLicenseDateRequired) : ''
  let otherlicenseDateErrMsg = OtherLicenseDateHasBlurred ? validateDateField(attorney?.license?.dateLicensedInOtherState, false, 10, isLicensedInOtherState) : ''

  return (
    <AboutAttorneyFormContainer>
      <LeftBar />
      <div className={formContainerClasses}>
        <div className='form-title'>
          <StepBullet isComplete>1</StepBullet>
          <h6>LICENSING</h6>
          <button onClick={toggleOpenAccordion}>
            {status.isOpen ? (
              <MdKeyboardArrowUp />
            ) : (
              <MdKeyboardArrowDown />
            )}
          </button>
        </div>
        <div className='form-content'>
          <OptionButtonsContainer>
            <span>Is this attorney currently licensed in Texas?</span>
            <div>
              <OptionButton
                checked={attorney?.license?.isLicensedInTexas === true}
                onChange={() => handleInputLicensedInTexasChange({ isLicensedInTexas: true })}
                isIncorrect={isInputIncorrect() && attorney?.license?.isLicensedInTexas === undefined}
                errorCode={errorCodes[23]}
              >
                Yes
              </OptionButton>
              <OptionButton
                checked={attorney?.license?.isLicensedInTexas === false}
                onChange={() => handleInputLicensedInTexasChange({ isLicensedInTexas: false })}
                isIncorrect={isInputIncorrect() && attorney?.license?.isLicensedInTexas === undefined}
                errorCode={errorCodes[23]}
              >
                No
              </OptionButton>
              {isInputIncorrect() && attorney?.license?.isLicensedInTexas === undefined ? (
                <FormInputErrorMessage>
                  This is a required field
                </FormInputErrorMessage>
              ) : null}
            </div>
          </OptionButtonsContainer>
          {!!attorney?.license?.isLicensedInTexas && (
            <>
              <BarCardNumberInput
                name="barCardNumber"
                label='Texas Bar Card Number'
                onChange={handleBarCardNumberChange}
                value={attorney?.barCardNumber ?? ''}
                errorCode={errorCodes[2]}
              />
              <StandardFormatInput
                label='Date licensed in Texas'
                maskStyle='date'
                value={attorney?.license?.licenseDate ?? ''}
                leftIcon="calendar"
                onValueChange={({ formattedValue }) => handleInputChange({ licenseDate: formattedValue })}
                isIncorrect={isLicenseDateIncorrect()}
                onBlur={handleLicenseDateBlur}
                errorMessage={licenseDateErrMsg}
                errorCode={errorCodes[6]}
              />
            </>
          )}
          <OptionButtonsContainer>
            <span>Is this attorney currently licensed in a state other than Texas?</span>
            <div>
              <OptionButton
                checked={attorney?.license?.isLicensedInOtherState === true}
                onChange={() => handleInputLicensedInOtherStateChange({ isLicensedInOtherState: true })}
                isIncorrect={isInputIncorrect() && attorney?.license?.isLicensedInOtherState === undefined}
                errorCode={errorCodes[23]}
              >
                Yes
              </OptionButton>
              <OptionButton
                checked={attorney?.license?.isLicensedInOtherState === false}
                onChange={() => handleInputLicensedInOtherStateChange({ isLicensedInOtherState: false })}
                isIncorrect={isInputIncorrect() && attorney?.license?.isLicensedInOtherState === undefined}
                errorCode={errorCodes[23]}
              >
                No
              </OptionButton>
            </div>
            <ValidationErrorMessage errorCode={errorCodes[23]} />
          </OptionButtonsContainer>

          {!!attorney?.license?.isLicensedInOtherState && (
            <>
              {/* other state */}
              <BarCardNumberInput
                name="outSideTexasBarCardNumber"
                label='Other State Bar Card Number'
                isOutsideTexas={true}
                onChange={handleBarCardNumberChange}
                value={attorney?.outSideTexasBarCardNumber ?? ''}
                errorCode={errorCodes[24]}
              />
              <StandardSelect
                label='State'
                name="state"
                value={attorney?.license?.otherState ?? ''}
                onChange={e => handleInputChange({ otherState: e.target.value })}
                onBlur={handleOtherStateBlur}
                isIncorrect={otherStateHasBlurred && !attorney?.license?.otherState}
                errorMessage={otherStateHasBlurred && !attorney?.license?.otherState ? 'This is a required field' : ''}
                errorCode={errorCodes[8]}
              >
                <option disabled value="">Select</option>
                {states.map((position, index) => (
                  <option key={index} value={position.abbreviation}>{position.name}</option>
                ))}
              </StandardSelect>

              <StandardFormatInput
                label='Date licensed in selected State'
                maskStyle='date'
                value={attorney?.license?.dateLicensedInOtherState ?? ''}
                leftIcon="calendar"
                onValueChange={({ formattedValue }) => handleInputChange({ dateLicensedInOtherState: formattedValue })}
                isIncorrect={isLicenseDateIncorrect()}
                onBlur={handleOtherLicenseDateBlur}
                errorMessage={otherlicenseDateErrMsg}
                errorCode={errorCodes[9]}
              />

              <StandardInput
                label={
                  <>
                    Percentage of time "<u>CURRENTLY</u>" practicing in this jurisdiction'
                  </>
                }
                value={attorney?.license?.percentageOfWorkSpent}
                onChange={e => handleInputChange({ percentageOfWorkSpent: e.target.value })}
                onBlur={handlePercentageOfWorkSpentBlur}
                isIncorrect={percentageOfWorkSpentHasBlurred && !attorney?.license?.percentageOfWorkSpent}
                errorMessage={percentageOfWorkSpentHasBlurred && !attorney?.license?.percentageOfWorkSpent ? 'This is a required field' : ''}
                errorCode={errorCodes[10]}
              />
              {/* other state end */}
              <FlexDiv gap="24px">
                <LeftBar />
                <FlexDiv>

                  <StandardInput
                    label="If licensed to practice law in any additional states or jurisdictions than the ones reported above, please provide the other date(s) licensed and whether these license(s) are still active. If any are currently active, please provide the % of your work spent in each jurisdiction."
                    rows={4}
                    value={attorney?.license?.licensesInfo ?? ''}
                    onChange={(e) => handleInputChange({ licensesInfo: e.target.value })}
                    isIncorrect={isInputIncorrect() && !attorney?.license?.licensesInfo}
                    errorMessage={isInputIncorrect() && !attorney?.license?.licensesInfo ? 'This is a required field' : ''}
                    placeholder=""
                    errorCode={errorCodes[11]}
                  />

                </FlexDiv>
              </FlexDiv>
            </>
          )}
          <ValidationErrorMessage errorCode={errorCodes[18]} />
          {/* <Button
            onClick={() => onSaveDetailsClick()}
            disabled={disabledSaveDetailsButton ? true : !isFormValid}
          >
            {disabledSaveDetailsButton ? 'Saving...' : 'Continue'}
          </Button> */}
        </div>
      </div>
    </AboutAttorneyFormContainer>
  )
}

export default Licensing